import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { API } from "@/lib/adminApi";

// Injects raw HTML and executes any <script> tags (for AdSense / custom JS).
const RawHtml = ({ html }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = html || "";
    el.querySelectorAll("script").forEach((old) => {
      const s = document.createElement("script");
      [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
      s.text = old.text;
      old.replaceWith(s);
    });
  }, [html]);
  return <div ref={ref} />;
};

const track = (id, kind) => {
  if (!id || id === "preview") return;
  axios.post(`${API}/ads/${id}/${kind}`).catch(() => {});
};

const ClickLink = ({ ad, children, className }) => (
  <a
    href={ad.link_url || "#"}
    target={ad.new_tab ? "_blank" : "_self"}
    rel="noopener noreferrer sponsored"
    onClick={() => track(ad.id, "click")}
    className={className}
    data-testid={`ad-link-${ad.id}`}
  >
    {children}
  </a>
);

export const AdRenderer = ({ ad, preview = false }) => {
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    if (!preview && ad?.id) track(ad.id, "impression");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ad?.id]);

  if (!ad || closed) return null;
  const t = ad.type;

  if (t === "image-banner" || t === "affiliate-banner") {
    return (
      <ClickLink ad={ad} className="block">
        {ad.image_url
          ? <img src={ad.image_url} alt={ad.name} className="mx-auto max-h-full max-w-full object-contain" />
          : <span className="text-xs text-muted-foreground">No image set</span>}
      </ClickLink>
    );
  }

  if (t === "affiliate-button") {
    return (
      <ClickLink ad={ad} className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-white transition-opacity hover:opacity-90">
        {ad.cta_text || ad.name || "Learn more"}
      </ClickLink>
    );
  }

  if (t === "affiliate-card" || t === "native") {
    return (
      <div className="flex w-full items-center gap-4 rounded-xl border border-border bg-card p-4 text-left">
        {ad.image_url && <img src={ad.image_url} alt={ad.name} className="h-16 w-16 shrink-0 rounded-lg object-cover" />}
        <div className="min-w-0 flex-1">
          <p className="truncate font-heading text-sm font-bold">{ad.name}</p>
          {ad.html && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{ad.html}</p>}
          <ClickLink ad={ad} className="mt-2 inline-flex text-xs font-bold text-primary hover:underline">
            {ad.cta_text || "View"} →
          </ClickLink>
        </div>
      </div>
    );
  }

  if (t === "html-banner" || t === "inline-content" || t === "ad-manager") {
    return <div className="w-full"><RawHtml html={ad.html || ad.script} /></div>;
  }

  if (t === "adsense") {
    const inner = ad.script
      || `<ins class="adsbygoogle" style="display:block" data-ad-client="${ad.adsense_client}" data-ad-slot="${ad.adsense_slot}" data-ad-format="${ad.adsense_format || "auto"}" data-full-width-responsive="true"></ins><script>(adsbygoogle=window.adsbygoogle||[]).push({});</script>`;
    return <div className="w-full"><RawHtml html={inner} /></div>;
  }

  if (t === "video") {
    if (ad.video_type === "youtube") {
      const id = (ad.video_url || "").split(/v=|\/embed\/|youtu\.be\//).pop().split(/[?&]/)[0];
      return <iframe title={ad.name} className="aspect-video w-full rounded-lg" src={`https://www.youtube.com/embed/${id}?autoplay=${ad.autoplay ? 1 : 0}&mute=${ad.muted ? 1 : 0}&loop=${ad.loop ? 1 : 0}`} allow="autoplay; encrypted-media" allowFullScreen />;
    }
    if (ad.video_type === "vimeo") {
      const id = (ad.video_url || "").split("/").pop();
      return <iframe title={ad.name} className="aspect-video w-full rounded-lg" src={`https://player.vimeo.com/video/${id}?autoplay=${ad.autoplay ? 1 : 0}&muted=${ad.muted ? 1 : 0}`} allow="autoplay; fullscreen" />;
    }
    return <video className="aspect-video w-full rounded-lg" src={ad.video_url} controls autoPlay={ad.autoplay} muted={ad.muted} loop={ad.loop} onClick={() => track(ad.id, "click")} />;
  }

  if (t === "announcement-bar") {
    return (
      <div data-testid="ad-announcement" className="flex w-full items-center justify-center gap-3 bg-primary px-4 py-2.5 text-center text-sm font-medium text-white">
        <span>{ad.html || ad.name}</span>
        {ad.link_url && <ClickLink ad={ad} className="rounded-full bg-white/20 px-3 py-1 text-xs font-bold hover:bg-white/30">{ad.cta_text || "Learn more"}</ClickLink>}
        {!preview && <button onClick={() => setClosed(true)} className="ml-2 opacity-80 hover:opacity-100" aria-label="Dismiss"><X className="h-4 w-4" /></button>}
      </div>
    );
  }

  // popup / sticky-footer / sticky-sidebar / floating share a generic card body
  return (
    <div className="relative w-full">
      {ad.image_url
        ? <ClickLink ad={ad} className="block"><img src={ad.image_url} alt={ad.name} className="mx-auto max-w-full" /></ClickLink>
        : ad.html
          ? <RawHtml html={ad.html} />
          : <ClickLink ad={ad} className="inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white">{ad.cta_text || ad.name}</ClickLink>}
    </div>
  );
};
